import { isMacintosh } from 'vscode/vscode/vs/base/common/platform';
import { AbstractGotoLineQuickAccessProvider } from 'vscode/vscode/vs/editor/contrib/quickAccess/browser/gotoLineQuickAccess';
import { localize2WithPath, localizeWithPath } from 'vscode/vscode/vs/nls';
import { Extensions as Extensions$2 } from 'vscode/vscode/vs/platform/configuration/common/configurationRegistry';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { SyncDescriptor } from 'vscode/vscode/vs/platform/instantiation/common/descriptors';
import { Extensions as Extensions$1 } from 'vscode/vscode/vs/platform/quickinput/common/quickAccess';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { ViewPaneContainer } from 'vscode/vscode/vs/workbench/browser/parts/views/viewPaneContainer';
import { defaultQuickAccessContextKeyValue } from 'vscode/vscode/vs/workbench/browser/quickaccess';
import { Extensions } from 'vscode/vscode/vs/workbench/common/views';
import { GotoSymbolQuickAccessProvider } from 'vscode/vscode/vs/workbench/contrib/codeEditor/browser/quickaccess/gotoSymbolQuickAccess';
import { AnythingQuickAccessProvider } from './anythingQuickAccess.js';
import { registerContributions } from './replaceContributions.js';
import 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import 'vscode/vscode/vs/workbench/contrib/search/browser/notebookSearch/notebookSearchService';
import { searchViewIcon } from './searchIcons.js';
import { SearchView } from './searchView.js';
import { registerContributions as registerContributions$1 } from './searchWidget.js';
import { SymbolsQuickAccessProvider } from './symbolsQuickAccess.js';
import '../common/searchHistoryService.js';
import 'vscode/vscode/vs/workbench/contrib/search/browser/searchModel';
import { VIEWLET_ID, VIEW_ID, SEARCH_EXCLUDE_CONFIG } from 'vscode/vscode/vs/workbench/services/search/common/search';
import { CommandsRegistry } from 'vscode/vscode/vs/platform/commands/common/commands';
import { assertType } from 'vscode/vscode/vs/base/common/types';
import { getWorkspaceSymbols } from 'vscode/vscode/vs/workbench/contrib/search/common/search';
import './searchActionsCopy.js';
import 'vscode/vscode/vs/workbench/contrib/search/browser/searchActionsFind';
import './searchActionsNav.js';
import './searchActionsRemoveReplace.js';
import './searchActionsSymbol.js';
import './searchActionsTopBar.js';
import './searchActionsTextQuickAccess.js';
import { TextSearchQuickAccess, TEXT_SEARCH_QUICK_ACCESS_PREFIX } from './quickTextSearch/textSearchQuickAccess.js';
import { Extensions as Extensions$3 } from 'vscode/vscode/vs/workbench/common/configuration';

const _moduleId = "vs/workbench/contrib/search/browser/search.contribution";
registerContributions();
registerContributions$1();
const SEARCH_MODE_CONFIG = 'search.mode';
const viewContainer = ( (Registry.as(Extensions.ViewContainersRegistry))).registerViewContainer({
    id: VIEWLET_ID,
    title: ( localize2WithPath(_moduleId, 0, "Search")),
    ctorDescriptor: ( (new SyncDescriptor(
        ViewPaneContainer,
        [VIEWLET_ID, { mergeViewWithContainerWhenSingleView: true }]
    ))),
    hideIfEmpty: true,
    icon: searchViewIcon,
    order: 1,
}, 0 , { doNotRegisterOpenCommand: true });
const viewDescriptor = {
    id: VIEW_ID,
    containerIcon: searchViewIcon,
    name: ( localize2WithPath(_moduleId, 0, "Search")),
    ctorDescriptor: ( (new SyncDescriptor(SearchView))),
    canToggleVisibility: false,
    canMoveView: true,
    openCommandActionDescriptor: {
        id: viewContainer.id,
        mnemonicTitle: ( localizeWithPath(_moduleId, 1, "&&Search")),
        keybindings: {
            primary: 2048  | 1024  | 36 ,
            when: ( (ContextKeyExpr.regex('neverMatch', /doesNotMatch/)))
        },
        order: 1
    }
};
( (Registry.as(Extensions.ViewsRegistry))).registerViews([viewDescriptor], viewContainer);
const quickAccessRegistry = ( (Registry.as(Extensions$1.Quickaccess)));
quickAccessRegistry.registerQuickAccessProvider({
    ctor: AnythingQuickAccessProvider,
    prefix: AnythingQuickAccessProvider.PREFIX,
    placeholder: ( localizeWithPath(
        _moduleId,
        2,
        "Search files by name (append {0} to go to line or {1} to go to symbol)",
        AbstractGotoLineQuickAccessProvider.PREFIX,
        GotoSymbolQuickAccessProvider.PREFIX
    )),
    contextKey: defaultQuickAccessContextKeyValue,
    helpEntries: [{
            description: ( localizeWithPath(_moduleId, 3, "Go to File")),
            commandId: 'workbench.action.quickOpen',
            commandCenterOrder: 10
        }]
});
quickAccessRegistry.registerQuickAccessProvider({
    ctor: SymbolsQuickAccessProvider,
    prefix: SymbolsQuickAccessProvider.PREFIX,
    placeholder: ( localizeWithPath(_moduleId, 4, "Type the name of a symbol to open.")),
    contextKey: 'inWorkspaceSymbolsPicker',
    helpEntries: [{ description: ( localizeWithPath(_moduleId, 5, "Go to Symbol in Workspace")), commandId: "workbench.action.showAllSymbols"  }]
});
quickAccessRegistry.registerQuickAccessProvider({
    ctor: TextSearchQuickAccess,
    prefix: TEXT_SEARCH_QUICK_ACCESS_PREFIX,
    contextKey: 'inTextSearchPicker',
    placeholder: ( localizeWithPath(_moduleId, 6, "Search for text in your workspace files.")),
    helpEntries: [
        {
            description: ( localizeWithPath(_moduleId, 7, "Search for Text")),
            commandId: "workbench.action.quickTextSearch" ,
            commandCenterOrder: 25,
        }
    ]
});
const configurationRegistry = ( (Registry.as(Extensions$2.Configuration)));
configurationRegistry.registerConfiguration({
    id: 'search',
    order: 13,
    title: ( localizeWithPath(_moduleId, 8, "Search")),
    type: 'object',
    properties: {
        [SEARCH_EXCLUDE_CONFIG]: {
            type: 'object',
            markdownDescription: ( localizeWithPath(
                _moduleId,
                9,
                "Configure [glob patterns](https://code.visualstudio.com/docs/editor/codebasics#_advanced-search-options) for excluding files and folders in fulltext searches and file search in quick open. To exclude files from the recently opened list in quick open, patterns must be absolute (for example `**/node_modules/**`). Inherits all glob patterns from the `#files.exclude#` setting."
            )),
            default: { '**/node_modules': true, '**/bower_components': true, '**/*.code-search': true },
            additionalProperties: {
                anyOf: [
                    {
                        type: 'boolean',
                        description: ( localizeWithPath(
                            _moduleId,
                            10,
                            "The glob pattern to match file paths against. Set to true or false to enable or disable the pattern."
                        )),
                    },
                    {
                        type: 'object',
                        properties: {
                            when: {
                                type: 'string',
                                pattern: '\\w*\\$\\(basename\\)\\w*',
                                default: '$(basename).ext',
                                markdownDescription: ( localizeWithPath(
                                    _moduleId,
                                    11,
                                    'Additional check on the siblings of a matching file. Use \\$(basename) as variable for the matching file name.'
                                ))
                            }
                        }
                    }
                ]
            },
            scope: 4
        },
        [SEARCH_MODE_CONFIG]: {
            type: 'string',
            enum: ['view', 'reuseEditor', 'newEditor'],
            default: 'view',
            markdownDescription: ( localizeWithPath(
                _moduleId,
                12,
                "Controls where new `Search: Find in Files` and `Find in Folder` operations occur: either in the search view, or in a search editor."
            )),
            enumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    13,
                    "Search in the search view, either in the panel or side bars."
                )),
                ( localizeWithPath(
                    _moduleId,
                    14,
                    "Search in an existing search editor if present, otherwise in a new search editor."
                )),
                ( localizeWithPath(_moduleId, 15, "Search in a new search editor.")),
            ]
        },
        'search.useRipgrep': {
            type: 'boolean',
            description: ( localizeWithPath(
                _moduleId,
                16,
                "This setting is deprecated and now falls back on \"search.usePCRE2\"."
            )),
            deprecationMessage: ( localizeWithPath(
                _moduleId,
                17,
                "Deprecated. Consider \"search.usePCRE2\" for advanced regex feature support."
            )),
            default: true
        },
        'search.maintainFileSearchCache': {
            type: 'boolean',
            deprecationMessage: ( localizeWithPath(
                _moduleId,
                18,
                "The search cache is kept in the extension host which never shuts down, so this setting is no longer needed."
            )),
            description: ( localizeWithPath(
                _moduleId,
                19,
                "When enabled, the searchService process will be kept alive instead of being shut down after an hour of inactivity. This will keep the file search cache in memory."
            )),
            default: false
        },
        'search.useIgnoreFiles': {
            type: 'boolean',
            markdownDescription: ( localizeWithPath(
                _moduleId,
                20,
                "Controls whether to use `.gitignore` and `.ignore` files when searching for files."
            )),
            default: true,
            scope: 4
        },
        'search.useGlobalIgnoreFiles': {
            type: 'boolean',
            markdownDescription: ( localizeWithPath(
                _moduleId,
                21,
                "Controls whether to use your global gitignore file (for example, from `$HOME/.config/git/ignore`) when searching for files. Requires `#search.useIgnoreFiles#` to be enabled."
            )),
            default: false,
            scope: 4
        },
        'search.useParentIgnoreFiles': {
            type: 'boolean',
            markdownDescription: ( localizeWithPath(
                _moduleId,
                22,
                "Controls whether to use `.gitignore` and `.ignore` files in parent directories when searching for files. Requires `#search.useIgnoreFiles#` to be enabled."
            )),
            default: false,
            scope: 4
        },
        'search.quickOpen.includeSymbols': {
            type: 'boolean',
            description: ( localizeWithPath(
                _moduleId,
                23,
                "Whether to include results from a global symbol search in the file results for Quick Open."
            )),
            default: false
        },
        'search.quickOpen.includeHistory': {
            type: 'boolean',
            description: ( localizeWithPath(
                _moduleId,
                24,
                "Whether to include results from recently opened files in the file results for Quick Open."
            )),
            default: true
        },
        'search.quickOpen.history.filterSortOrder': {
            type: 'string',
            enum: ['default', 'recency'],
            default: 'default',
            enumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    25,
                    'History entries are sorted by relevance based on the filter value used. More relevant entries appear first.'
                )),
                ( localizeWithPath(
                    _moduleId,
                    26,
                    'History entries are sorted by recency. More recently opened entries appear first.'
                ))
            ],
            description: ( localizeWithPath(
                _moduleId,
                27,
                "Controls sorting order of editor history in quick open when filtering."
            ))
        },
        'search.followSymlinks': {
            type: 'boolean',
            description: ( localizeWithPath(_moduleId, 28, "Controls whether to follow symlinks while searching.")),
            default: true
        },
        'search.smartCase': {
            type: 'boolean',
            description: ( localizeWithPath(
                _moduleId,
                29,
                "Search case-insensitively if the pattern is all lowercase, otherwise, search case-sensitively."
            )),
            default: false
        },
        'search.globalFindClipboard': {
            type: 'boolean',
            default: false,
            description: ( localizeWithPath(
                _moduleId,
                30,
                "Controls whether the search view should read or modify the shared find clipboard on macOS."
            )),
            included: isMacintosh
        },
        'search.location': {
            type: 'string',
            enum: ['sidebar', 'panel'],
            default: 'sidebar',
            description: ( localizeWithPath(
                _moduleId,
                31,
                "Controls whether the search will be shown as a view in the sidebar or as a panel in the panel area for more horizontal space."
            )),
            deprecationMessage: ( localizeWithPath(
                _moduleId,
                32,
                "This setting is deprecated. You can drag the search icon to a new location instead."
            ))
        },
        'search.maxResults': {
            type: ['number', 'null'],
            default: 20000,
            markdownDescription: ( localizeWithPath(
                _moduleId,
                33,
                "Controls the maximum number of search results, this can be set to `null` (empty) to return unlimited results."
            ))
        },
        'search.collapseResults': {
            type: 'string',
            enum: ['auto', 'alwaysCollapse', 'alwaysExpand'],
            enumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    34,
                    "Files with less than 10 results are expanded. Others are collapsed."
                )),
                '',
                ''
            ],
            default: 'alwaysExpand',
            description: ( localizeWithPath(
                _moduleId,
                35,
                "Controls whether the search results will be collapsed or expanded."
            )),
        },
        'search.useReplacePreview': {
            type: 'boolean',
            default: true,
            description: ( localizeWithPath(
                _moduleId,
                36,
                "Controls whether to open Replace Preview when selecting or replacing a match."
            )),
        },
        'search.showLineNumbers': {
            type: 'boolean',
            default: false,
            description: ( localizeWithPath(_moduleId, 37, "Controls whether to show line numbers for search results.")),
        },
        'search.usePCRE2': {
            type: 'boolean',
            default: false,
            description: ( localizeWithPath(
                _moduleId,
                38,
                "Whether to use the PCRE2 regex engine in text search. This enables using some advanced regex features like lookahead and backreferences. However, not all PCRE2 features are supported - only features that are also supported by JavaScript."
            )),
            deprecationMessage: ( localizeWithPath(
                _moduleId,
                39,
                "Deprecated. PCRE2 will be used automatically when using regex features that are only supported by PCRE2."
            )),
        },
        'search.actionsPosition': {
            type: 'string',
            enum: ['auto', 'right'],
            enumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    40,
                    "Position the actionbar to the right when the search view is narrow, and immediately after the content when the search view is wide."
                )),
                ( localizeWithPath(_moduleId, 41, "Always position the actionbar to the right.")),
            ],
            default: 'right',
            description: ( localizeWithPath(
                _moduleId,
                42,
                "Controls the positioning of the actionbar on rows in the search view."
            ))
        },
        'search.searchOnType': {
            type: 'boolean',
            default: true,
            description: ( localizeWithPath(_moduleId, 43, "Search all files as you type."))
        },
        'search.seedWithNearestWord': {
            type: 'boolean',
            default: false,
            description: ( localizeWithPath(
                _moduleId,
                44,
                "Enable seeding search from the word nearest the cursor when the active editor has no selection."
            ))
        },
        'search.seedOnFocus': {
            type: 'boolean',
            default: false,
            markdownDescription: ( localizeWithPath(
                _moduleId,
                45,
                "Update the search query to the editor's selected text when focusing the search view. This happens either on click or when triggering the `workbench.views.search.focus` command."
            ))
        },
        'search.searchOnTypeDebouncePeriod': {
            type: 'number',
            default: 300,
            markdownDescription: ( localizeWithPath(
                _moduleId,
                46,
                "When {0} is enabled, controls the timeout in milliseconds between a character being typed and the search starting. Has no effect when {0} is disabled.",
                '`#search.searchOnType#`'
            ))
        },
        'search.searchEditor.doubleClickBehaviour': {
            type: 'string',
            enum: ['selectWord', 'goToLocation', 'openLocationToSide'],
            default: 'goToLocation',
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 47, "Double-clicking selects the word under the cursor.")),
                ( localizeWithPath(
                    _moduleId,
                    48,
                    "Double-clicking opens the result in the active editor group."
                )),
                ( localizeWithPath(
                    _moduleId,
                    49,
                    "Double-clicking opens the result in the editor group to the side, creating one if it does not yet exist."
                )),
            ],
            markdownDescription: ( localizeWithPath(
                _moduleId,
                50,
                "Configure effect of double-clicking a result in a search editor."
            ))
        },
        'search.searchEditor.singleClickBehaviour': {
            type: 'string',
            enum: ['default', 'peekDefinition',],
            default: 'default',
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 51, "Single-clicking does nothing.")),
                ( localizeWithPath(_moduleId, 52, "Single-clicking opens a Peek Definition window.")),
            ],
            markdownDescription: ( localizeWithPath(
                _moduleId,
                53,
                "Configure effect of single-clicking a result in a search editor."
            ))
        },
        'search.searchEditor.reusePriorSearchConfiguration': {
            type: 'boolean',
            default: false,
            markdownDescription: ( localizeWithPath(
                _moduleId,
                54,
                "When enabled, new Search Editors will reuse the includes, excludes, and flags of the previously opened Search Editor."
            ))
        },
        'search.searchEditor.defaultNumberOfContextLines': {
            type: ['number', 'null'],
            default: 1,
            markdownDescription: ( localizeWithPath(
                _moduleId,
                55,
                "The default number of surrounding context lines to use when creating new Search Editors. If using `#search.searchEditor.reusePriorSearchConfiguration#`, this can be set to `null` (empty) to use the prior Search Editor's configuration."
            ))
        },
        'search.sortOrder': {
            type: 'string',
            enum: ["default" , "fileNames" , "type" , "modified" , "countDescending" , "countAscending" ],
            default: "default" ,
            enumDescriptions: [
                ( localizeWithPath(
                    _moduleId,
                    56,
                    "Results are sorted by folder and file names, in alphabetical order."
                )),
                ( localizeWithPath(
                    _moduleId,
                    57,
                    "Results are sorted by file names ignoring folder order, in alphabetical order."
                )),
                ( localizeWithPath(
                    _moduleId,
                    58,
                    "Results are sorted by file extensions, in alphabetical order."
                )),
                ( localizeWithPath(
                    _moduleId,
                    59,
                    "Results are sorted by file last modified date, in descending order."
                )),
                ( localizeWithPath(
                    _moduleId,
                    60,
                    "Results are sorted by count per file, in descending order."
                )),
                ( localizeWithPath(_moduleId, 61, "Results are sorted by count per file, in ascending order."))
            ],
            description: ( localizeWithPath(_moduleId, 62, "Controls sorting order of search results."))
        },
        'search.decorations.colors': {
            type: 'boolean',
            description: ( localizeWithPath(
                _moduleId,
                63,
                "Controls whether search file decorations should use colors."
            )),
            default: true
        },
        'search.decorations.badges': {
            type: 'boolean',
            description: ( localizeWithPath(
                _moduleId,
                64,
                "Controls whether search file decorations should use badges."
            )),
            default: true
        },
        'search.defaultViewMode': {
            type: 'string',
            enum: ["tree" , "list" ],
            default: "list" ,
            enumDescriptions: [
                ( localizeWithPath(_moduleId, 65, "Shows search results as a tree.")),
                ( localizeWithPath(_moduleId, 66, "Shows search results as a list."))
            ],
            description: ( localizeWithPath(_moduleId, 67, "Controls the default search result view mode."))
        },
        'search.quickAccess.preserveInput': {
            type: 'boolean',
            description: ( localizeWithPath(
                _moduleId,
                68,
                "Controls whether the last typed input to Quick Search should be restored when opening it the next time."
            )),
            default: false
        },
        'search.experimental.closedNotebookRichContentResults': {
            type: 'boolean',
            description: ( localizeWithPath(
                _moduleId,
                69,
                "Show notebook editor rich content results for closed notebooks. Please refresh your search results after changing this setting."
            )),
            default: false
        },
    }
});
CommandsRegistry.registerCommand('_executeWorkspaceSymbolProvider', async function (accessor, ...args) {
    const [query] = args;
    assertType(typeof query === 'string');
    const result = await getWorkspaceSymbols(query);
    return (
         (result.map(item => item.symbol))
    );
});
( (Registry.as(Extensions$3.ConfigurationMigration)))
    .registerConfigurationMigrations([{
        key: 'search.experimental.quickAccess.preserveInput',
        migrateFn: (value, _accessor) => ([
            ['search.quickAccess.preserveInput', { value }],
            ['search.experimental.quickAccess.preserveInput', { value: undefined }]
        ])
    }]);
